import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import ClientProvider from "../modules/ClientProvider";
import { IScheduler, SchedulerType } from "../types";

interface UseSchedulersOptions {
    projectTag: string;
    types?: SchedulerType[];
    isPublic?: boolean;
    enabled?: boolean;
}

export const useSchedulers = ({ 
    projectTag, 
    types, 
    isPublic = false, 
    enabled = true 
}: UseSchedulersOptions) => {
    const [schedulers, setSchedulers] = useState<Record<SchedulerType, IScheduler | null>>({} as Record<SchedulerType, IScheduler | null>);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { getAccessTokenSilently } = useAuth0();

    // Stabilize the types array to prevent unnecessary re-renders
    const stableTypes = useMemo(() => types || [], [types]);

    const fetchSchedulers = useCallback(async () => {
        if (!projectTag || !enabled || !stableTypes.length) return;

        setLoading(true);
        setError(null);

        // Create client inside callback to avoid dependency issues
        const schedulerClient = ClientProvider.provideSchedulerClient();

        // Define fetchSchedulerByType inside the callback to avoid dependency issues
        const fetchSchedulerByType = async (type: SchedulerType): Promise<IScheduler | null> => {
            try {
                let response;

                if (isPublic) {
                    response = await schedulerClient.getPublicSchedulerByType(projectTag, type);
                } else {
                    const accessToken = await getAccessTokenSilently();
                    response = await schedulerClient.getSchedulerByType(projectTag, type, accessToken);
                }

                // Check if response exists and has data
                if (!response || !response.data) {
                    console.error(`No data returned for ${type} scheduler`);
                    return null;
                }

                const { data, error } = response;

                if (error) {
                    console.error(`Error fetching ${type} scheduler:`, error);
                    return null;
                } else {
                    const fetchedScheduler = data.scheduler;
                    // Only return scheduler if it has a valid scheduling URL
                    if (fetchedScheduler?.scheduling_url && fetchedScheduler.scheduling_url !== "") {
                        return fetchedScheduler;
                    } else {
                        return null;
                    }
                }
            } catch (err) {
                console.error(`Error fetching ${type} scheduler:`, err);
                return null;
            }
        };

        try {
            const schedulerPromises = stableTypes.map(async (type) => {
                try {
                    const scheduler = await fetchSchedulerByType(type);
                    return { type, scheduler };
                } catch (err) {
                    console.error(`Failed to fetch ${type} scheduler:`, err);
                    return { type, scheduler: null };
                }
            });

            const results = await Promise.all(schedulerPromises);
            
            const schedulersMap = results.reduce((acc, { type, scheduler }) => {
                acc[type] = scheduler;
                return acc;
            }, {} as Record<SchedulerType, IScheduler | null>);

            setSchedulers(schedulersMap);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Unknown error occurred";
            console.error("Error fetching schedulers:", err);
            setError(errorMessage);
            // Set empty schedulers map on error
            const emptySchedulersMap = stableTypes.reduce((acc, type) => {
                acc[type] = null;
                return acc;
            }, {} as Record<SchedulerType, IScheduler | null>);
            setSchedulers(emptySchedulersMap);
        } finally {
            setLoading(false);
        }
    }, [projectTag, stableTypes, isPublic, enabled]);

    const refetchScheduler = useCallback(async (type: SchedulerType) => {
        if (!projectTag || !enabled) return;

        const schedulerClient = ClientProvider.provideSchedulerClient();

        const fetchSchedulerByType = async (schedulerType: SchedulerType): Promise<IScheduler | null> => {
            try {
                let response;

                if (isPublic) {
                    response = await schedulerClient.getPublicSchedulerByType(projectTag, schedulerType);
                } else {
                    const accessToken = await getAccessTokenSilently();
                    response = await schedulerClient.getSchedulerByType(projectTag, schedulerType, accessToken);
                }

                if (!response || !response.data) {
                    return null;
                }

                const { data, error } = response;
                if (error) {
                    return null;
                }

                const fetchedScheduler = data.scheduler;
                if (fetchedScheduler?.scheduling_url && fetchedScheduler.scheduling_url !== "") {
                    return fetchedScheduler;
                }
                return null;
            } catch (err) {
                console.error(`Error fetching ${schedulerType} scheduler:`, err);
                return null;
            }
        };

        try {
            const scheduler = await fetchSchedulerByType(type);
            setSchedulers(prev => ({
                ...prev,
                [type]: scheduler
            }));
        } catch (err) {
            console.error(`Error refetching ${type} scheduler:`, err);
        }
    }, [projectTag, enabled, isPublic]);

    useEffect(() => {
        fetchSchedulers();
    }, [fetchSchedulers]);

    return {
        schedulers,
        loading,
        error,
        refetch: fetchSchedulers,
        refetchScheduler
    };
}; 